import { FieldType } from '@indigohive/cogfy-types'
import { GetOperationsResultData } from '@indigohive/cogfy-types/endpoints/getOperations'
import { createElement, Fragment, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { FIELD_ICONS_BY_TYPE } from '../../../../../../field-types'
import clsx from 'clsx'
import fieldTypeParsed from '../../../../../../field-types/locales/en.json'
import { FieldsMenuModuleProvider } from '../FieldsMenuModuleProvider/'
import { FieldsMenuOperationItem } from '../FieldsMenuOperationItem/'

export type FieldsMenuProps = {
  fieldTypesByCategory: FieldType[][]
  operations: GetOperationsResultData[]
  searchFieldName: string
  onClick?: (fieldType: FieldType, fieldOperation?: string, displayName?: string) => void
}

export function FieldsMenu (props: FieldsMenuProps) {
  const { fieldTypesByCategory, searchFieldName, onClick } = props

  const { t } = useTranslation()

  const operations = props.operations.map(operation => ({
    ...operation,
    displayName: operation.module ? `${operation.module}: ${operation.displayName}` : operation.displayName
  }))

  const filteredFields = useMemo(
    () => filterFieldsByName(fieldTypesByCategory, searchFieldName),
    [fieldTypesByCategory, searchFieldName]
  )
  const filteredOperations = useMemo(
    () => filterOperationsByName(operations, searchFieldName),
    [operations, searchFieldName]
  )

  const noFieldsFound = filteredFields.every(fieldArray => fieldArray.length === 0)

  return (
    <div className="overflow-y-auto max-h-[50vh]">
      <li className="menu-title">
        {t('Fields')}
      </li>
      {noFieldsFound && (
        <li>
          <a>
            {t('No fields found')}
          </a>
        </li>
      )}
      {filteredFields.map((field, index) => (
        <Fragment key={index}>
          {field.map(fieldType =>
            <li key={fieldType}>
              <a onClick={() => onClick?.(fieldType)}>
                {createElement(
                  FIELD_ICONS_BY_TYPE[fieldType],
                  { size: 16 }
                )}
                {fieldTypeParsed[fieldType]}
              </a>
            </li>
          )}
          {index < fieldTypesByCategory.length - 1 && (
            <div
              className={clsx(
                'divider h-1 my-1',
                searchFieldName && 'hidden'
              )}
            />
          )}
        </Fragment>
      ))}

      <li className="menu-title">
        {t('Operations')}
      </li>
      {filteredOperations
        .sort((a, b) => a.displayName.localeCompare(b.displayName))
        .filter(operation => !operation.module)
        .map(operation => (
          <FieldsMenuOperationItem
            key={operation.name}
            operation={operation}
            onClick={onClick}
          />)
        )}
      {filteredOperations && <FieldsMenuModuleProvider operations={filteredOperations} onClick={onClick}/>}
      {filteredOperations.length === 0 && (
        <li>
          <a>
            {t('No operations found')}
          </a>
        </li>
      )}
    </div>
  )
}

function filterFieldsByName (
  fieldTypesByCategory: FieldType[][],
  searchFieldName: string
): FieldType[][] {
  if (!searchFieldName) {
    return fieldTypesByCategory
  }

  return fieldTypesByCategory.map(fieldTypes =>
    fieldTypes.filter(fieldType =>
      fieldType.toLowerCase().includes(searchFieldName.toLowerCase())
    )
  )
}

function filterOperationsByName (
  operations: GetOperationsResultData[],
  searchFieldName: string
): GetOperationsResultData[] {
  if (!searchFieldName) {
    return operations
  }

  return operations.filter(operation =>
    operation.displayName.toLowerCase().includes(searchFieldName.toLowerCase())
  )
}
